import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  Shield, 
  Users, 
  Building2, 
  FileText, 
  DollarSign,
  TrendingUp,
  LogOut,
  LayoutDashboard,
  ShoppingBag,
  CreditCard,
  UserPlus,
  ScrollText
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";

const AdminDashboardPage = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [admin, setAdmin] = useState(null);
  const navigate = useNavigate();

  const fetchStats = useCallback(async (token) => {
    try {
      const response = await axios.get(`${API}/admin/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem("adminToken");
        localStorage.removeItem("adminData");
        navigate("/admin/login");
      } else {
        toast.error("Failed to load dashboard");
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    const adminData = localStorage.getItem("adminData");
    const token = localStorage.getItem("adminToken");
    if (!token || !adminData) {
      navigate("/admin/login");
      return;
    }
    setAdmin(JSON.parse(adminData));
    fetchStats(token);
  }, [navigate, fetchStats]);

  const handleLogout = () => {
    localStorage.removeItem("adminToken");
    localStorage.removeItem("adminData");
    toast.success("Logged out successfully");
    navigate("/admin/login");
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-violet-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Admin Navbar */}
      <nav className="bg-slate-900/80 backdrop-blur-xl border-b border-slate-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-violet-500/20 flex items-center justify-center">
                <Shield className="w-6 h-6 text-violet-400" />
              </div>
              <div>
                <span className="text-lg font-bold text-white">Admin Portal</span>
                <Badge className="ml-2 bg-violet-500/20 text-violet-400 border-violet-500/30 text-xs">
                  {admin?.role}
                </Badge>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <span className="text-slate-400 text-sm hidden sm:block">{admin?.email}</span>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={handleLogout}
                className="text-slate-400 hover:text-white"
                data-testid="admin-logout-btn"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Navigation Tabs */}
        <div className="flex flex-wrap gap-2 mb-8">
          <Link to="/admin/dashboard">
            <Button className="bg-violet-600 hover:bg-violet-700 text-white">
              <LayoutDashboard className="w-4 h-4 mr-2" />
              Dashboard
            </Button>
          </Link>
          <Link to="/admin/users">
            <Button variant="outline" className="btn-secondary">
              <Users className="w-4 h-4 mr-2" />
              Users
            </Button>
          </Link>
          <Link to="/admin/requests">
            <Button variant="outline" className="btn-secondary">
              <ShoppingBag className="w-4 h-4 mr-2" />
              Service Requests
            </Button>
          </Link>
          <Link to="/admin/transactions">
            <Button variant="outline" className="btn-secondary">
              <CreditCard className="w-4 h-4 mr-2" />
              Transactions
            </Button>
          </Link>
          {admin?.role === "super_admin" && (
            <Link to="/admin/admins">
              <Button variant="outline" className="btn-secondary">
                <UserPlus className="w-4 h-4 mr-2" />
                Admins
              </Button>
            </Link>
          )}
          <Link to="/admin/audit">
            <Button variant="outline" className="btn-secondary">
              <ScrollText className="w-4 h-4 mr-2" />
              Audit Log
            </Button>
          </Link>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className="card-dashboard border-violet-500/20">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-slate-400 text-sm">Total Users</p>
                  <p className="text-3xl font-bold text-white mt-1">{stats?.users?.total || 0}</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-violet-500/10 flex items-center justify-center">
                  <Users className="w-6 h-6 text-violet-400" />
                </div>
              </div>
              <div className="mt-4 flex items-center gap-2">
                <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/30">
                  <UserPlus className="w-3 h-3 mr-1" />
                  +{stats?.users?.new_this_week || 0} this week
                </Badge>
              </div>
            </CardContent>
          </Card>

          <Card className="card-dashboard">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-slate-400 text-sm">Total Assets</p>
                  <p className="text-3xl font-bold text-white mt-1">{stats?.assets?.total || 0}</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-primary" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-dashboard">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-slate-400 text-sm">Service Requests</p>
                  <p className="text-3xl font-bold text-white mt-1">{stats?.service_requests?.total || 0}</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-amber-500/10 flex items-center justify-center">
                  <FileText className="w-6 h-6 text-amber-500" />
                </div>
              </div>
              <div className="mt-4">
                <Badge className="bg-amber-500/10 text-amber-400 border-amber-500/30">
                  {stats?.service_requests?.pending || 0} pending
                </Badge>
              </div>
            </CardContent>
          </Card>

          <Card className="card-dashboard">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-slate-400 text-sm">Total Revenue</p>
                  <p className="text-3xl font-bold text-white mt-1 mono">
                    {formatCurrency(stats?.revenue?.total || 0)}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-emerald-500" />
                </div>
              </div>
              <div className="mt-4">
                <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/30">
                  {stats?.revenue?.transactions || 0} transactions
                </Badge>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Subscription Breakdown */}
        <div className="grid lg:grid-cols-2 gap-6">
          <Card className="card-dashboard">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-violet-400" />
                User Subscriptions
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 rounded-xl bg-slate-800/50">
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 rounded-full bg-slate-500"></div>
                    <span className="text-white">Free</span>
                  </div>
                  <span className="text-2xl font-bold text-white">{stats?.users?.free || 0}</span>
                </div>
                <div className="flex items-center justify-between p-4 rounded-xl bg-primary/10 border border-primary/30">
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 rounded-full bg-primary"></div>
                    <span className="text-white">Professional</span>
                  </div>
                  <span className="text-2xl font-bold text-white">{stats?.users?.professional || 0}</span>
                </div>
                <div className="flex items-center justify-between p-4 rounded-xl bg-violet-500/10 border border-violet-500/30">
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 rounded-full bg-violet-500"></div>
                    <span className="text-white">Enterprise</span>
                  </div>
                  <span className="text-2xl font-bold text-white">{stats?.users?.enterprise || 0}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-dashboard">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <ShoppingBag className="w-5 h-5 text-amber-500" />
                Service Request Status
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 rounded-xl bg-amber-500/10 border border-amber-500/30">
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 rounded-full bg-amber-500"></div>
                    <span className="text-white">Pending</span>
                  </div>
                  <span className="text-2xl font-bold text-amber-400">{stats?.service_requests?.pending || 0}</span>
                </div>
                <div className="flex items-center justify-between p-4 rounded-xl bg-blue-500/10 border border-blue-500/30">
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                    <span className="text-white">In Progress</span>
                  </div>
                  <span className="text-2xl font-bold text-blue-400">{stats?.service_requests?.in_progress || 0}</span>
                </div>
                <div className="flex items-center justify-between p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/30">
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 rounded-full bg-emerald-500"></div>
                    <span className="text-white">Completed</span>
                  </div>
                  <span className="text-2xl font-bold text-emerald-400">{stats?.service_requests?.completed || 0}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboardPage;
